import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ChatContainer from '../components/Chat/ChatContainer';
import NicknameModal from '../components/NicknameModal';
import usePlatformSettings from '../components/Admin/usePlatformSettings';
import InteractivePollCard from '../components/Stream/InteractivePollCard';
import UpcomingEventsWidget from '../components/Stream/UpcomingEventsWidget';

const { FiPlay, FiVolume2, FiVolumeX } = FiIcons;

export default function LivePlatform() {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  
  const storedNickname = localStorage.getItem('gallo_nickname') || '';
  const [isLive, setIsLive] = useState(false);
  const [nickname, setNickname] = useState(storedNickname);
  const [showModal, setShowModal] = useState(false);
  const [needsUserTap, setNeedsUserTap] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // URL del Logo Oficial
  const LOGO_URL = "https://media-manager-c.questera.ai/greta-media/786069decac80ebfda9758781ecad48f576fd486e338363dff55357567d6916b51c713fb933d539c7b707900e265a4cf/images/aW1hZ2UvcG5n/05487cee64958a73423d5bdd3c770274.png";

  // Estado del tema: 'light' o 'dark'
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('gallo_theme') || 'dark';
  });

  // Obtener la configuración global en tiempo real
  const { settings, loading: settingsLoading } = usePlatformSettings();

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    localStorage.setItem('gallo_theme', theme);
  }, [theme]);

  useEffect(() => {
    let mounted = true;

    const initPlayer = () => {
      if (!mounted || !videoRef.current || !settings?.stream_url) return;
      const video = videoRef.current;
      const streamUrl = settings.stream_url;

      // Limpiar instancia previa de hls.js
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      const retry = () => {
        if (mounted) setIsLive(false);
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(initPlayer, 5000);
      };

      if (!settings.is_active) {
        if (mounted) setIsLive(false);
        return;
      }

      // Configuración de atributos y propiedades iniciales del elemento video
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', 'true');

      // ======================================================
      // 1. IPHONE / IPAD / SAFARI
      // HLS NATIVO (Prioridad obligatoria para Apple WebKit)
      // ======================================================
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('Usando HLS nativo de Safari/iOS');

        video.src = streamUrl;

        const handleLoadedMetadata = () => {
          if (!mounted) return;
          setIsLive(true);
          const playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                if (mounted) setNeedsUserTap(false);
              })
              .catch(() => {
                console.log('Safari requiere interacción del usuario');
                if (mounted) setNeedsUserTap(true);
              });
          }
        };

        const handlePlaying = () => {
          if (mounted) {
            setIsLive(true);
            setNeedsUserTap(false);
          }
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('error', retry, { once: true });
      } 
      // ======================================================
      // 2. ANDROID / CHROME / EDGE / FIREFOX
      // HLS.JS (Media Source Extensions)
      // ======================================================
      else if (Hls.isSupported()) {
        console.log('Usando HLS.js');

        const hls = new Hls({
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 6,
          enableWorker: true,
          lowLatencyMode: true
        });

        hlsRef.current = hls;
        hls.loadSource(streamUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!mounted) return;
          setIsLive(true);
          const playPromise = video.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                if (mounted) setNeedsUserTap(false);
              })
              .catch(() => {
                console.log('Autoplay bloqueado');
                if (mounted) setNeedsUserTap(true);
              });
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                retry();
                break;
            }
          }
        });
      } else {
        console.error('Este navegador no soporta HLS');
        if (mounted) setIsLive(false);
      }
    };

    initPlayer();

    return () => {
      mounted = false;
      clearTimeout(retryTimeoutRef.current);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [settings?.stream_url, settings?.is_active]);

  useEffect(() => {
    if (settings?.title) {
      document.title = settings.title;
    }
  }, [settings?.title]);

  const handleUserPlay = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.play().then(() => {
        setIsLive(true);
        setNeedsUserTap(false);
        setIsMuted(false);
      }).catch(() => {
        // En caso de que la política del sistema impida audio al iniciar
        videoRef.current.muted = true;
        videoRef.current.play();
        setIsLive(true);
        setNeedsUserTap(false);
      });
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      const nextMuted = !videoRef.current.muted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
    }
  };

  const saveNickname = (name) => {
    setNickname(name);
    localStorage.setItem('gallo_nickname', name);
    setShowModal(false);
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="fixed inset-0 flex flex-col lg:flex-row bg-black font-sans transition-colors duration-200">
      <NicknameModal isOpen={showModal} initialName={nickname} onSelect={saveNickname} onClose={() => setShowModal(false)} />
      
      {/* Columna Principal: Streaming ocupando el 100% de la columna */}
      <div className="relative flex-1 min-h-0 flex flex-col bg-black overflow-hidden">
        
        {/* Contenedor del Reproductor de Video */}
        <div className="relative w-full h-full flex items-center justify-center bg-black">
          <video 
            id="haciendaLiveVideo"
            ref={videoRef} 
            controls 
            autoPlay 
            muted 
            playsInline 
            webkit-playsinline="true"
            x5-playsinline="true"
            preload="auto"
            className={`w-full h-full object-contain ${!settings?.is_active ? 'hidden' : ''}`} 
          />

          {/* Botón de reproducción asistida para iOS / Safari si bloquea autoplay */}
          {needsUserTap && settings?.is_active && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm p-4 text-center">
              <button
                type="button"
                onClick={handleUserPlay}
                className="group flex flex-col items-center gap-3 p-6 rounded-3xl bg-red-600/90 hover:bg-red-600 text-white shadow-2xl transition-all transform active:scale-95"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-red-600 shadow-lg group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiPlay} size={32} className="ml-1" />
                </div>
                <span className="text-base font-bold uppercase tracking-wider">Tocar para reproducir</span>
                <span className="text-xs text-red-100">Transmisión en Vivo disponible</span>
              </button>
            </div>
          )}

          {/* Botón de Activar Audio si está silenciado */}
          {isLive && isMuted && (
            <button
              onClick={toggleSound}
              className="absolute top-4 left-4 z-30 flex items-center gap-2 rounded-full bg-black/70 hover:bg-black/90 text-white px-3 py-1.5 text-xs font-semibold backdrop-blur-md border border-white/20 transition-all shadow-lg"
            >
              <SafeIcon icon={FiVolumeX} size={14} className="text-red-500" />
              <span>Activar Sonido</span>
            </button>
          )}

          {(!isLive || !settings?.is_active) && !settingsLoading && !needsUserTap && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black p-6 text-center backdrop-blur-sm">
              {settings?.is_active === false ? (
                <>
                  <img 
                    src={LOGO_URL} 
                    alt="Hacienda El Gallo" 
                    className="w-64 h-64 md:w-[400px] md:h-[400px] mb-8 opacity-80 drop-shadow-2xl animate-pulse object-contain" 
                  />
                  <h2 className="mb-2 text-xl font-bold text-white md:text-3xl tracking-wide"> Transmisión Pausada </h2>
                  <p className="text-sm text-gray-400 max-w-md">
                    La transmisión se encuentra fuera de línea en este momento. Por favor, espera a que el evento se reanude.
                  </p>
                </>
              ) : (
                <>
                  <img 
                    src={LOGO_URL} 
                    alt="Hacienda El Gallo" 
                    className="w-56 h-56 md:w-[350px] md:h-[350px] mb-8 opacity-50 drop-shadow-2xl object-contain grayscale" 
                  />
                  <h2 className="mb-2 text-xl font-bold text-white md:text-3xl"> {settings?.title || 'Transmisión no disponible'} </h2>
                  <p className="text-sm text-gray-500">Intentando conectar señal...</p>
                  <div className="mt-8 flex items-center gap-3 text-gray-600">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                    <span className="text-[11px] md:text-xs uppercase tracking-widest font-bold"> Reconectando </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Widgets Flotantes Superpuestos al Video */}
          <div className="absolute bottom-6 left-6 right-6 z-30 flex flex-col sm:flex-row items-end justify-between gap-4 pointer-events-none">
            <div className="pointer-events-auto">
              <InteractivePollCard />
            </div>
            <div className="pointer-events-auto">
              <UpcomingEventsWidget />
            </div>
          </div>
        </div>
      </div>

      {/* Columna Lateral: Chat Multiusuario */}
      <div className="flex w-full flex-col h-[45vh] lg:h-full lg:w-[350px] xl:w-[400px] shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-white/10 shadow-2xl z-40 bg-white dark:bg-[#0a0a0a]">
        <ChatContainer 
          currentUser={nickname} 
          onChangeNickname={() => setShowModal(true)} 
          theme={theme}
          toggleTheme={toggleTheme}
        />
      </div>
    </div>
  );
}